import { DateTime } from 'luxon';

import { type DateTimeFieldFormat } from '@/types/schema/fields';
import { getTimezoneConfig } from '@/utils/timezone';
import {
  DEFAULT_LUXON_DATE,
  getLuxonDateFormatFromFieldFormat
} from '@/components/knack-date-picker/transformers/DateTimeFormatToLuxonFormat';
import { type DateTimeAttributes } from '@/components/knack-date-picker/types';
import { sanitizeDateString } from '@/components/knack-date-picker/utils';

// IMPORTANT: the transformer is breaking the UTC time zone to make sure the date is not converted to the local time zone
// This is because the server is not handling the time zone correctly
export function KnackDateToJsDateTransformer(
  knackDate: DateTimeAttributes,
  fieldFormat: DateTimeFieldFormat
) {
  if (!knackDate) {
    return undefined;
  }

  // Instead of converting the variables names to our guidelines, keep it like it comes from the server for searchability
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { iso_timestamp, unix_timestamp, date, hours, minutes, am_pm } = knackDate;

  // The properties `iso_timestamp` and `unix_timestamp` are only available when fetching a record value from the server.
  // Other places in the schema where we date/time value is stored (record rules, conditional rules, etc) will not have these properties.
  if (iso_timestamp) {
    // In the future we will be able to just do:
    // return DateTime.fromISO(iso_timestamp).toJSDate();

    // The server is sending the date in UTC, but the time zone is not being handled correctly
    // So we want the browser to ignore the time zone
    // So we convert the ISO date to a string forcing the UTC time zone
    // Then we convert the string to a date using the current time zone
    const forcedUTCtoString = DateTime.fromISO(
      iso_timestamp,
      getTimezoneConfig().zoneOptions
    ).toFormat('MM/dd/yyyy HH:mm');
    const fromStringToCurrentISODate = DateTime.fromFormat(forcedUTCtoString, 'MM/dd/yyyy HH:mm', {
      // zone: '' //  (Not setting the zone will convert it to the local one)
    }).toJSDate();

    return fromStringToCurrentISODate;
  }

  if (unix_timestamp) {
    return DateTime.fromMillis(unix_timestamp, getTimezoneConfig().zoneOptions).toJSDate();
  }

  // Checking the existence and truthiness of the `hours` and `minutes` properties, since they could be a number zero (0)
  const hasHours = hours !== undefined && hours !== '';
  const hasMinutes = minutes !== undefined && minutes !== '';
  const hasTime = hasHours && hasMinutes && am_pm;

  const normalizedDateString = sanitizeDateString(date, fieldFormat);
  const normalizedDateFormat =
    getLuxonDateFormatFromFieldFormat(fieldFormat?.date_format) || DEFAULT_LUXON_DATE;

  let normalizedTimeFormat = '';
  if (fieldFormat?.time_format === 'HH:MM am') {
    normalizedTimeFormat = 'hh:mma';
  } else if (fieldFormat?.time_format === 'HH MM (military)') {
    normalizedTimeFormat = 'HH:mm';
  }

  // If we have a date, we convert it to a JS date with the proper time (if available)
  if (normalizedDateString) {
    let month: number;
    let day: number;
    let year: number;

    if (normalizedDateFormat === 'MM/dd/yyyy') {
      [month, day, year] = normalizedDateString.split('/').map(Number);
    } else {
      [day, month, year] = normalizedDateString.split('/').map(Number);
    }

    const dateTimeFormatForConversion = hasTime
      ? `${normalizedDateFormat} ${normalizedTimeFormat}`
      : normalizedDateFormat;

    // The hours should be fixed to 24-hour format but the server could send it in 12-hour format
    // So if the hours are less than 12 and the am_pm is PM, we add 12 to the hours
    let fixedHours = Number(hours);
    if (fixedHours && Number(fixedHours) < 12 && am_pm === 'PM') {
      fixedHours += 12;
    }

    const forcedUTCtoString = DateTime.fromObject(
      {
        year,
        month,
        day,
        ...(hasTime ? { hour: fixedHours, minute: Number(minutes) } : {})
      },
      getTimezoneConfig().zoneOptions
    ).toFormat(dateTimeFormatForConversion);

    const fromStringToCurrentISODate = DateTime.fromFormat(
      forcedUTCtoString,
      dateTimeFormatForConversion,
      {
        // zone: '' //  (Not setting the zone will convert it to the local one)
      }
    ).toJSDate();

    return fromStringToCurrentISODate;
  }

  // If we only have a time, we return a JS date using the current date
  if (hasTime) {
    const currentDateTime = DateTime.local();

    const forcedUTCtoString = currentDateTime
      .set({
        hour: Number(hours),
        minute: Number(minutes)
      })
      .toFormat(normalizedTimeFormat);

    const fromStringToCurrentISODate = DateTime.fromFormat(
      forcedUTCtoString,
      normalizedTimeFormat,
      {
        // zone: '' //  (Not setting the zone will convert it to the local one)
      }
    ).toJSDate();

    return fromStringToCurrentISODate;
  }

  return undefined;
}
